import './Search.css';
import CreateReturnModal from '../Upload/Upload.js';
import SERVER_URL from '../../../../env.js';
import { useEffect, useState, useCallback } from 'react'
import Cookies from 'js-cookie';

function FeeBox({ fees }) {
    const [expanded, setExpanded] = useState(false);

    if (!fees || fees.length === 0) {
        return <div className="return-fees" style={{ cursor: 'default' }}>No fees</div>;
    }

    if (fees.length === 1) {
        return (
            <div className="return-fees" style={{ cursor: 'default' }}>
                <div className="fee-item">{fees[0].fee_type}: ${fees[0].amount}</div>
            </div>
        );
    }

    return (
        <div className="return-fees" onClick={() => setExpanded(!expanded)}>
            <div><strong className="text-color--muted-orange">Fees:</strong></div>
            {fees[0].fee_type}: ${fees[0].amount} {expanded ? '' : ` (+${fees.length - 1} more)`}
            {expanded && fees.slice(1).map((fee, index) => (
                <div key={index} className="fee-item">
                    <div className="fee-item__name">{fee.fee_type}:</div>
                    <div className="fee-item__amount"> ${fee.amount}</div>
                </div>
            ))}
        </div>
    );
}


function Search({ toggleNav }) {
    const [returns, setReturns] = useState([])
    const [searchName, setSearchName] = useState('')
    const [searchYear, setSearchYear] = useState('')
    const [loading, setLoading] = useState(true)
    const [updateMessage, setUpdateMessage] = useState('');

    const STATUS_OPTIONS = ['Open', 'Closed', 'Pending', 'Awaiting Documents'];

    // Edit modal state
    const [editingReturn, setEditingReturn] = useState(null)
    const [editInvoiceNumber, setEditInvoiceNumber] = useState('')
    const [editStatus, setEditStatus] = useState('Open')
    const [editCash, setEditCash] = useState('')
    const [editETransferOrCheque, setEditETransferOrCheque] = useState('')
    const [editLastContactDate, setEditLastContactDate] = useState('')
    const [editNotes, setEditNotes] = useState('')
    const [editFees, setEditFees] = useState([])
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [saveError, setSaveError] = useState('')

    // Return modal
    const [addReturnModalOpen, setAddReturnModal] = useState(false)


    const searchFilter = useCallback((ret) => {
        if (!searchName && !searchYear) {
            return true
        }
        if (searchName && !ret?.client_name?.toLowerCase().includes(searchName?.toLowerCase())) {
            return false
        }
        if (searchYear && !ret?.year?.toString().includes(searchYear?.toLowerCase())) {
            return false
        }
        return true
    }, [searchName, searchYear])

    const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            return d.toISOString().split('T')[0];
        } catch (e) {
            return '';
        }
    }

    const openEdit = (ret) => {
        setEditingReturn(ret)
        setEditInvoiceNumber(ret.invoice_number || '')
        setEditStatus(ret.status || 'Open')
        setEditCash(ret.cash !== null && ret.cash !== undefined ? ret.cash : '')
        setEditETransferOrCheque(ret.e_transfer_or_cheque !== null && ret.e_transfer_or_cheque !== undefined ? ret.e_transfer_or_cheque : '')
        setEditLastContactDate(formatDateForInput(ret.last_contact_date))
        setEditNotes(ret.notes || '')
        setEditFees(ret.fees ? ret.fees.map(f => ({ id: f.id, fee_type: f.fee_type, amount: f.amount })) : [])
        setSaveError('')
        setDeleting(false)
    }

    const openAddReturnModal = () => {
        setAddReturnModal(true)
    }

    const closeAddReturnModal = () => {
        setAddReturnModal(false)
    }

    const closeEdit = () => {
        setEditingReturn(null)
        setEditInvoiceNumber('')
        setEditStatus('Open')
        setEditCash('')
        setEditETransferOrCheque('')
        setEditLastContactDate('')
        setEditNotes('')
        setEditFees([])
        setSaveError('')
        setDeleting(false)
    }

    const handleAddFee = () => {
        setEditFees(prev => [...prev, { fee_type: '', amount: '' }])
    }

    const handleRemoveFee = (index) => {
        setEditFees(prev => prev.filter((_, idx) => idx !== index))
    }

    const handleFeeChange = (index, field, value) => {
        setEditFees(prev => prev.map((fee, idx) => idx === index ? { ...fee, [field]: value } : fee))
    }

    const saveEdit = async () => {
        setSaving(true)
        setSaveError('')
        try {
            const formattedFees = editFees
                .filter(f => f.fee_type.trim() !== '')
                .map(f => ({
                    id: f.id || undefined,
                    fee_type: f.fee_type.trim(),
                    amount: parseFloat(f.amount) || 0
                }));

            const response = await fetch(SERVER_URL + '/returns/' + editingReturn.id, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
                body: JSON.stringify({
                    cash: editCash === '' ? 0 : parseFloat(editCash),
                    e_transfer_or_cheque: editETransferOrCheque === '' ? 0 : parseFloat(editETransferOrCheque),
                    outstanding_balance: (
                        editFees
                            .filter(f => f.fee_type.trim() !== '')
                            .reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0) -
                        (editCash === '' ? 0 : parseFloat(editCash)) -
                        (editETransferOrCheque === '' ? 0 : parseFloat(editETransferOrCheque))
                    ),
                    invoice_number: editInvoiceNumber || null,
                    last_contact_date: editLastContactDate || null,
                    notes: editNotes,
                    status: editStatus,
                    fees: formattedFees,
                }),
            })
            const data = await response.json()
            if (data.error) {
                setSaveError(data.error)
            } else {
                setReturns((prev) =>
                    prev.map((r) => (r.id === editingReturn.id ? { ...r, ...data.return } : r))
                )
                closeEdit()
            }
        } catch (err) {
            setSaveError('An unexpected error occurred.')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!editingReturn) return;

        const confirmDelete = window.confirm(
            `Are you sure you want to delete the return for ${editingReturn.client_name} (Year: ${editingReturn.year})? This will also delete all associated fees and cannot be undone.`
        );
        if (!confirmDelete) return;

        setDeleting(true)
        setSaveError('')
        try {
            const response = await fetch(SERVER_URL + '/returns/' + editingReturn.id, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
            })
            const data = await response.json()
            if (data.error) {
                setSaveError(data.error)
            } else {
                setReturns((prev) => prev.filter((r) => r.id !== editingReturn.id))
                closeEdit()
            }
        } catch (err) {
            setSaveError('An unexpected error occurred while deleting.')
        } finally {
            setDeleting(false)
        }
    }

    useEffect(() => {
        fetch(SERVER_URL + '/returns', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + Cookies.get('accessToken'),
            },
        })
            .then(async (response) => {
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || errData.message || `HTTP error ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setReturns(data.returns);
            })
            .catch((error) => {
                setUpdateMessage(prev => prev + `\nError loading returns: ${error.message}`);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [])

    const fmt = (val) => {
        const n = parseFloat(val);
        return isNaN(n) ? '0.00' : n.toFixed(2);
    };


    return (
        <>
            <div className="returns-search-container">
                <input type="text" className="returns-search-input" placeholder="Client Name..." onChange={(e) => setSearchName(e.target.value)} />
                <input type="number" min={1900} className="returns-search-input year-input" placeholder="Year..." onChange={(e) => setSearchYear(e.target.value)} />
            </div>

            <div className="rs-create-btn-container">
                <button className="rs-btn-add" onClick={openAddReturnModal}>Add Return</button>
            </div>

            {updateMessage && <div style={{ color: 'red', textAlign: 'center' }}>{updateMessage}</div>}

            {loading ? (
                <div className="loading-container">
                    <div className="loading">Loading...</div>
                </div>
            ) : returns.length > 0 ? (
                <div className="return-container">
                    {returns.filter((ret) => searchFilter(ret)).map((ret, index) => (
                        <div key={index} className='return-row'>
                            <div className='return-main'>
                                <div className='return-meta'>
                                    {ret.client_name && <span className="return-tag name-tag">{ret.client_name}</span>}
                                    {ret.year && <span className="return-tag year-tag">{ret.year}</span>}
                                    <span className={`return-tag status-tag status-${(ret.status || 'Open').toLowerCase().replace(/\s+/g, '-')}`}>
                                        {ret.status || 'Open'}
                                    </span>
                                </div>
                                <div key={ret.id} className="cr-row-returns">
                                    <div className="cr-row-main">
                                        {/* meta tags */}
                                        <div className="cr-meta">
                                            {ret.invoice_number && (
                                                <span className="cr-tag cr-tag--invoice">Invoice #{ret.invoice_number}</span>
                                            )}
                                            {ret.last_contact_date && (
                                                <span className="cr-tag cr-tag--date">
                                                    Last Contact: {new Date(ret.last_contact_date).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>

                                        {/* financials */}
                                        <div className="cr-financials">
                                            {parseFloat(ret.cash) > 0 && (
                                                <div className="cr-fin-item">
                                                    <span className="cr-fin-label">Cash</span>
                                                    <span className="cr-fin-val">${fmt(ret.cash)}</span>
                                                </div>
                                            )}
                                            {parseFloat(ret.e_transfer_or_cheque) > 0 && (
                                                <div className="cr-fin-item">
                                                    <span className="cr-fin-label">E-Transfer / Cheque</span>
                                                    <span className="cr-fin-val">${fmt(ret.e_transfer_or_cheque)}</span>
                                                </div>
                                            )}
                                            {parseFloat(ret.outstanding_balance) > 0 && (
                                                <div className="cr-fin-item cr-fin-item--outstanding">
                                                    <span className="cr-fin-label">Outstanding</span>
                                                    <span className="cr-fin-val">${fmt(ret.outstanding_balance)}</span>
                                                </div>
                                            )}
                                        </div>
                                        {/* notes */}
                                        {ret.notes && (
                                            <div className="cr-notes">
                                                <strong>Notes:</strong> {ret.notes}
                                            </div>
                                        )}


                                    </div>
                                </div>
                                <FeeBox fees={ret.fees} />
                            </div>
                            <div className='return-actions'>
                                <button className='modal-btn modal-btn-cancel' onClick={() => openEdit(ret)}>Edit</button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="no-returns-container">
                    <div className="no-returns">No returns found.</div>
                </div>
            )}

            {editingReturn && (
                <div className="modal-overlay" onClick={closeEdit}>
                    <div className="modal" style={{ width: 'min(640px, 95vw)' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h2 className="modal-title">Edit Return</h2>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#888' }}>
                                    {editingReturn.client_name} &mdash; Year: {editingReturn.year}
                                </p>
                            </div>
                            <button className="modal-close" onClick={closeEdit}>&#x2715;</button>
                        </div>

                        <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto', gap: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label className="modal-label" style={{ marginTop: 0 }}>Invoice Number</label>
                                    <input
                                        className="return-modal-input"
                                        type="text"
                                        value={editInvoiceNumber}
                                        onChange={(e) => setEditInvoiceNumber(e.target.value)}
                                        placeholder="Invoice Number..."
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label className="modal-label" style={{ marginTop: 0 }}>Status</label>
                                    <select
                                        className="return-modal-input"
                                        value={editStatus}
                                        onChange={(e) => setEditStatus(e.target.value)}
                                        style={{ background: 'transparent', color: '#fefefe' }}
                                    >
                                        {STATUS_OPTIONS.map(opt => (
                                            <option key={opt} value={opt} style={{ background: '#1a1a1a', color: '#fefefe' }}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label className="modal-label" style={{ marginTop: 0 }}>Last Contact Date</label>
                                    <input
                                        className="return-modal-input"
                                        type="date"
                                        value={editLastContactDate}
                                        onChange={(e) => setEditLastContactDate(e.target.value)}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label className="modal-label" style={{ marginTop: 0 }}>Cash ($)</label>
                                    <input
                                        className="return-modal-input"
                                        type="number"
                                        step="0.01"
                                        value={editCash}
                                        onChange={(e) => setEditCash(e.target.value)}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label className="modal-label" style={{ marginTop: 0 }}>E-Transfer / Cheque ($)</label>
                                    <input
                                        className="return-modal-input"
                                        type="number"
                                        step="0.01"
                                        value={editETransferOrCheque}
                                        onChange={(e) => setEditETransferOrCheque(e.target.value)}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <label className="modal-label" style={{ marginTop: 0 }}>Outstanding Balance ($)</label>
                                    <div className="return-calculated-field">
                                        ${(
                                            editFees
                                                .filter(f => f.fee_type.trim() !== '')
                                                .reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0) -
                                            (editCash === '' ? 0 : parseFloat(editCash)) -
                                            (editETransferOrCheque === '' ? 0 : parseFloat(editETransferOrCheque))
                                        ).toFixed(2)}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <label className="modal-label">Notes</label>
                                <textarea
                                    className="modal-textarea"
                                    value={editNotes}
                                    onChange={(e) => setEditNotes(e.target.value)}
                                    placeholder="Notes..."
                                />
                            </div>

                            {/* Fees Section */}
                            <div style={{ marginTop: '1rem', borderTop: '1px solid #333', paddingTop: '1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                    <h3 style={{ margin: 0, fontSize: '0.85rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Fees</h3>
                                    <button
                                        type="button"
                                        className="modal-btn"
                                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem', background: '#333', color: '#f48120', border: '1px solid #f48120' }}
                                        onClick={handleAddFee}
                                    >
                                        + Add Fee
                                    </button>
                                </div>

                                {editFees.length === 0 ? (
                                    <p style={{ color: '#666', fontSize: '0.9rem', margin: '0.5rem 0 0' }}>No fees added.</p>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {editFees.map((fee, idx) => (
                                            <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <input
                                                    className="return-modal-input"
                                                    style={{ flex: 2 }}
                                                    type="text"
                                                    placeholder="Fee Type (e.g. T1 Fee)"
                                                    value={fee.fee_type}
                                                    onChange={(e) => handleFeeChange(idx, 'fee_type', e.target.value)}
                                                />
                                                <input
                                                    className="return-modal-input"
                                                    style={{ flex: 1 }}
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Amount"
                                                    value={fee.amount}
                                                    onChange={(e) => handleFeeChange(idx, 'amount', e.target.value)}
                                                />
                                                <button
                                                    type="button"
                                                    className="modal-btn modal-btn-cancel"
                                                    style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onClick={() => handleRemoveFee(idx)}
                                                    aria-label="Remove Fee"
                                                >
                                                    &#x2715;
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {saveError && <p className="modal-error">{saveError}</p>}
                        </div>

                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-delete"
                                onClick={handleDelete}
                                disabled={saving || deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                            <div className="modal-footer-right" style={{ marginLeft: 'auto' }}>
                                <button className="modal-btn modal-btn-cancel" onClick={closeEdit} disabled={saving || deleting}>
                                    Cancel
                                </button>
                                <button className="modal-btn modal-btn-save" onClick={saveEdit} disabled={saving || deleting}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {addReturnModalOpen && (
                <div className="modal-overlay" onClick={closeAddReturnModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Add Return</h2>
                            <button className="modal-close" onClick={closeAddReturnModal}>&#x2715;</button>
                        </div>
                        <div className="modal-body">
                            <CreateReturnModal closeCreate={closeAddReturnModal} setReturns={setReturns} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Search;
